import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import SEO from '../components/SEO';
import { seoData } from '../data/seoData';
import { BookOpen, Users, Code, Cloud, Shield, Settings, Database, Cpu, ChevronDown, ChevronUp } from 'lucide-react';

const learningPaths = [
  {
    id: 1,
    title: "Cloud Support Engineer",
    emoji: "👨‍💻",
    courses: 6,
    projects: 15,
    price: 299,
    description: "Start your cloud career with hands-on support skills and interview prep",
    color: "bg-blue-50",
    courseList: [
      "AWS Interview Mastery (Get Hired!)",
      "Study Notes: AWS Certified Cloud Practitioner",
      "AWS Certified Cloud Practitioner Practice Tests",
      "5 AWS Cloud Projects to Become a Cloud Support Engineer",
      "Build: 5 Beginner AWS Cloud Projects",
      "Build: 5 Intermediate AWS Cloud Projects"
    ]
  },
  {
    id: 2,
    title: "Solutions Architect", 
    emoji: "👷‍♀️",
    courses: 8,
    projects: 15,
    price: 299,
    description: "Design scalable cloud architectures and ace your interviews",
    color: "bg-purple-50",
    courseList: [
      "AWS Interview Mastery (Get Hired!)",
      "Study Notes: AWS Certified Cloud Practitioner",
      "AWS Certified Cloud Practitioner Practice Tests",
      "Study Notes: AWS Certified Solutions Architect - Associate",
      "AWS Certified Solutions Architect - Associate Practice Tests",
      "Build: 5 Beginner AWS Cloud Projects",
      "5 AWS Cloud Projects to Become a Solutions Architect",
      "Build: 5 Intermediate AWS Cloud Projects"
    ]
  },
  {
    id: 3,
    title: "Cloud Engineer",
    emoji: "🧑‍💼",
    courses: 6,
    projects: 15,
    price: 299,
    description: "Build and manage cloud infrastructure with interview confidence",
    color: "bg-green-50",
    courseList: [
      "AWS Interview Mastery (Get Hired!)",
      "Study Notes: AWS Certified Cloud Practitioner",
      "AWS Certified Cloud Practitioner Practice Tests",
      "Build: 5 Beginner AWS Cloud Projects",
      "Build: 5 Intermediate AWS Cloud Projects",
      "5 AWS Projects to Become a Cloud Engineer"
    ]
  },
  {
    id: 4,
    title: "Cloud AI / ML Engineer",
    emoji: "🧑‍🔬",
    courses: 8,
    projects: 15,
    price: 299,
    description: "Develop AI solutions in the cloud and nail technical interviews",
    color: "bg-yellow-50",
    courseList: [
      "AWS Interview Mastery (Get Hired!)",
      "Study Notes: AWS Certified Cloud Practitioner",
      "Study Notes: AWS Certified AI Practitioner",
      "AWS Certified AI Practitioner Practice Tests",
      "Build: 5 Beginner AWS Cloud Projects",
      "Build: 5 Intermediate AWS Cloud Projects",
      "5 AWS Projects to Become a Cloud AI/ML Engineer"
    ]
  },
  {
    id: 5,
    title: "Cloud Security Engineer",
    emoji: "🔒",
    courses: 6,
    projects: 15,
    price: 299,
    description: "Secure cloud infrastructure and prepare for security interviews",
    color: "bg-red-50",
    courseList: [
      "AWS Interview Mastery (Get Hired!)",
      "Study Notes: AWS Certified Cloud Practitioner",
      "AWS Certified Cloud Practitioner Practice Tests",
      "Build: 5 Beginner AWS Cloud Projects",
      "Build: 5 Intermediate AWS Cloud Projects",
      "5 AWS Projects to Become a Cloud Security Engineer"
    ]
  },
  {
    id: 6,
    title: "DevOps Engineer",
    emoji: "🚀",
    courses: 7,
    projects: 15,
    price: 299,
    description: "Build CI/CD pipelines and automation for DevOps roles",
    color: "bg-orange-50",
    courseList: [
      "AWS Interview Mastery (Get Hired!)",
      "Study Notes: AWS Certified Cloud Practitioner",
      "Study Notes: AWS Certified CloudOps Engineer - Associate",
      "AWS Certified Cloud Practitioner Practice Tests",
      "Build: 5 Beginner AWS Cloud Projects",
      "Build: 5 Intermediate AWS Cloud Projects",
      "5 AWS Cloud Projects to Become a DevOps Engineer"
    ]
  }
];

const PathCard = ({ path, isExpanded, onToggle }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover-lift overflow-hidden border border-gray-100 p-6">
      {/* Main content - horizontal layout */}
      <div className="flex items-start gap-4 mb-4">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${path.color}`}>
          <span className="text-2xl">{path.emoji}</span>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {path.title}
          </h3>
          
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            {path.description}
          </p>
          
          {/* Stats - horizontal */}
          <div className="flex items-center flex-wrap gap-4 text-sm mb-4">
            <div className="flex items-center gap-1.5 text-gray-600">
              <BookOpen size={16} className="text-blue-500" />
              <span className="font-medium">{path.courses} Courses</span>
            </div>
            <div className="w-px h-4 bg-gray-300"></div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <Code size={16} className="text-purple-500" />
              <span className="font-medium">{path.projects} Projects</span>
            </div>
            <div className="w-px h-4 bg-gray-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-xl font-bold text-green-600">${path.price}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Course Dropdown */}
      <div className="mb-4">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between text-left text-purple-600 hover:text-purple-700 font-medium text-sm py-2 px-3 rounded-lg hover:bg-purple-50 transition-colors"
        >
          <span>View Course Details</span>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        
        {isExpanded && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3 text-sm">Included Courses:</h4>
            <ul className="space-y-2">
              {path.courseList.map((course, index) => (
                <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  {course}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* CTA Button */}
      <button 
        onClick={() => {
          const pathRoutes = {
            "Cloud Support Engineer": "/learning-paths/cloud-support-engineer",
            "Solutions Architect": "/learning-paths/solutions-architect",
            "Cloud Engineer": "/learning-paths/cloud-engineer", 
            "Cloud AI / ML Engineer": "/learning-paths/cloud-ai-ml-engineer",
            "Cloud Security Engineer": "/learning-paths/cloud-security-engineer",
            "DevOps Engineer": "/learning-paths/devops-engineer"
          };
          
          const route = pathRoutes[path.title];
          if (route) {
            window.location.href = route;
          }
        }}
        className="btn-outline-gradient w-full justify-center text-sm"
      >
        View Learning Path
      </button>
    </div>
  );
};

const LearningPathsPage = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const handleToggle = (pathId) => {
    setExpandedCard(expandedCard === pathId ? null : pathId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...seoData.learningPaths} />
      <PromoBanner />
      <Navigation />
      
      {/* Header Section */}
      <section className="pt-24 pb-8 bg-white">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="heading-large text-gray-900 mb-6">
              Choose your Future Role, Master Those Skills
            </h1>
            <p className="body-large text-gray-600 mb-8">
              Structured learning paths designed to take you from beginner to expert 
              in your chosen cloud career. Each path includes hands-on projects, 
              certifications, and real-world scenarios.
            </p>
          </div>
        </div>
      </section>

      {/* Learning Paths Grid */}
      <section className="pt-8 pb-20">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto justify-items-center">
            {learningPaths.map((path) => (
              <PathCard 
                key={path.id} 
                path={path} 
                isExpanded={expandedCard === path.id}
                onToggle={() => handleToggle(path.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Banner */}
      <section className="py-12 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-center shadow-lg">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                $299 to unlock resources for your dream role, lifetime access
              </h2>
              <p className="text-blue-100 text-lg">
                Choose your path below and start your cloud career journey today
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cloud Career Quiz CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-teal-50">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg border border-white/20">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="text-3xl">🤔</span>
              </div>
              
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Not sure which role to aim for?
              </h2>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Take our 1-minute cloud learner quiz and discover the perfect career path 
                tailored to your interests, skills, and goals!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="/cloud-role-quiz"
                  className="btn-primary text-lg px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  Take the Quiz Now →
                </a>
                <span className="text-sm text-gray-500 font-medium">
                  ⏱️ Only takes 60 seconds
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LearningPathsPage;